// Single source of truth for suggested criteria metadata
// Each item: { id, name, defaultPolarity, coreDimension, iconPath }
export const suggestedCriteria = [
  { id: '1', name: 'How will I feel about taking this path a year from now?', defaultPolarity: 'Higher is better', coreDimension: 'Long term benefit', iconPath: '/Icons/Growth icon.png' },
  { id: '2', name: 'What does my gut tell me?', defaultPolarity: 'Higher is better', coreDimension: 'Intuition', iconPath: '/Icons/Gut feeling icon.svg' },
  { id: '3', name: 'How much closer to my dreams does this path take me?', defaultPolarity: 'Higher is better', coreDimension: 'Trascendence', iconPath: '/Icons/Star icon.png' },
  { id: '4', name: 'How will it affect the people I care about?', defaultPolarity: 'Higher is better', coreDimension: 'Impact on relationships', iconPath: '/Icons/Heart icon.svg' },
  { id: '5', name: 'How much does it cost? (money, time, attention...)', defaultPolarity: 'Lower is better', coreDimension: 'Cost', iconPath: '/Icons/Cost icon.svg' },
  { id: '6', name: 'How risky or uncertain is it?', defaultPolarity: 'Lower is better', coreDimension: 'Risk', iconPath: '/Icons/Risk icon.svg' },
  { id: '7', name: 'Would I recommend this path to my best friend if he faced this dilemma?', defaultPolarity: 'Higher is better', coreDimension: 'Outside perspective', iconPath: '/Icons/Best friend icon.svg' },
  { id: '8', name: 'How much fun is it?', defaultPolarity: 'Higher is better', coreDimension: 'Enjoyment', iconPath: '/Icons/Fun icon.svg' },
  { id: '9', name: 'How many valuable opportunities could this path open?', defaultPolarity: 'Higher is better', coreDimension: 'Opportunity investment', iconPath: '/Icons/Opportunity cost icon.svg' },
];

// Legacy: derive name -> defaultPolarity map
export const suggestedItemsMap = Object.fromEntries(
  suggestedCriteria.map((i) => [i.name, i.defaultPolarity])
);

// Legacy: derive icon mapping (name -> iconPath) and include default fallback
const baseIconMapping = Object.fromEntries(
  suggestedCriteria.map((i) => [i.name, i.iconPath])
);
export const iconMapping = { ...baseIconMapping, default: '/Icons/Custom Criteria Icon - Signpost base.svg' };

// Example information keyed by coreDimension
const exampleInfoByDimension = {
  'Long term benefit': {
    exampleInfo: 'Taking the job would definitely mean a step forward in my career, providing money and contacts valuable for the future so it scores way higher than rejecting',
    category: 'Critical',
    exampleId: '1'  // Reference to the suggestedCriteria ID
  },
  'Impact on relationships': {
    exampleInfo: 'Rejecting the offer would mean staying as I am so leaving its score unchanged seems fair. On the other hand, moving would mean much less time with my loved ones',
    category: 'Critical',
    exampleId: '4'
  },
  'Risk': {
    exampleInfo: 'Using staying on my job as a standard, moving seems a bit risky because I might not like the new city but trying to negotiate new conditions would be riskier because I might alienate my current company and also have the offer dropped',
    category: 'Important',
    exampleId: '6'
  }
};

// Custom examples not in suggestedCriteria
const customExamples = [
  {
    id: 'expected_stress',
    name: 'Expected stress',
    coreDimension: 'Stress',
    exampleInfo: 'My current position is under control so it scores the best, accepting the offer would mean more responsibility and also adapting to a new city',
    polarity: 'Lower is better',
    category: 'Important'
  }
];

// Validate that all required example properties are present
const validateExample = (example, source) => {
  const requiredProps = ['id', 'name', 'coreDimension', 'exampleInfo', 'category', 'polarity'];
  const missingProps = requiredProps.filter(prop => example[prop] === undefined);
  
  if (missingProps.length > 0) {
    console.warn(`Example ${example.id} from ${source} is missing required properties:`, missingProps);
    return false;
  }
  return true;
};

// Build CriteriaAndTipsExample by combining suggestedCriteria with example info
// and adding custom examples
const buildCriteriaAndTips = () => {
  if (!Array.isArray(suggestedCriteria)) {
    console.error('suggestedCriteria must be an array');
    return [];
  }

  if (!Array.isArray(customExamples)) {
    console.error('customExamples must be an array');
    return [];
  }

  // Process criteria examples with validation
  const criteriaExamples = suggestedCriteria
    .filter(criterion => {
      if (!criterion || !criterion.coreDimension) {
        console.warn('Invalid criterion object:', criterion);
        return false;
      }
      
      const hasExample = exampleInfoByDimension[criterion.coreDimension];
      if (!hasExample) {
        console.warn(`No example info for core dimension: ${criterion.coreDimension}`);
        return false;
      }
      return true;
    })
    .map(criterion => {
      const example = {
        ...criterion,
        ...exampleInfoByDimension[criterion.coreDimension],
        // Ensure we always have a polarity
        polarity: criterion.defaultPolarity || 'Higher is better'
      };
      
      validateExample(example, `suggested criteria (${criterion.id})`);
      return example;
    });
  
  // Validate custom examples
  const validCustomExamples = customExamples
    .filter(example => {
      if (!example || !example.id) {
        console.warn('Invalid custom example:', example);
        return false;
      }
      return validateExample(example, 'custom examples');
    });
  
  return [...criteriaExamples, ...validCustomExamples];
};

// Export the built examples, ensuring we always return an array
export const CriteriaAndTipsExample = (() => {
  try {
    const examples = buildCriteriaAndTips();
    return Array.isArray(examples) ? examples : [];
  } catch (error) {
    console.error('Error building CriteriaAndTipsExample:', error);
    return [];
  }
})();
